import { useEffect, useState } from 'react';
import { debounce, truncate } from '../../lib/Utils.js';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import Modal from '../Modal';
import CalendarDisableIcon from '../icons/CalendarDisableIcon.jsx';
import CalendarAddIcon from '../icons/CalendarAddIcon.jsx';
import {
    TypeToHumanName,
    TypeToIcon,
    TypeToIconSm,
} from '../../lib/CalendarSyncing.jsx';
import AddEventsToCalendarModal from './AddEventsToCalendarModal.jsx';
import CheckConflictsModal from './CheckConflictsModal.jsx';
import { setCalendarIntegrationsQuery } from '~/lib/Queries';

function accountsToOptions(accounts) {
    const options = [];
    for (const account of accounts) {
        const calendarOptions = {};
        for (const calendar of account.calendars) {
            calendarOptions[calendar.id] = {
                name: calendar.name,
                is_writeable: calendar.sync_is_writeable,
            };
        }

        options.push({
            id: account.id,
            name: account.name,
            type: account.type,
            options: calendarOptions,
        });
    }

    return options;
}

function calendarNameMap(accounts) {
    const calendarNameMap = {};

    for (const account of accounts) {
        for (const calendar of account.calendars) {
            calendarNameMap[calendar.id] = (
                <div className="flex items-center">
                    <div className="mr-2">{TypeToIconSm[account.type]}</div>
                    {calendar.name}
                </div>
            );
        }
    }

    return calendarNameMap;
}

const BookableResourceSyncing = ({ resource, accounts }) => {
    const [blockFromCalendars, setBlockFromCalendars] = useState([]);
    const [addEventToCalendar, setAddEventToCalendar] = useState(null);
    const { save, isSaving } = useBusinessPostApi(
        `/integrations/calendars/bookable-resources/${resource.id}`,
    );

    const [isAddEventsOpen, setIsAddEventsOpen] = useState(false);
    const [isConflictsOpen, setIsConflictsOpen] = useState(false);

    const accountOptions = accountsToOptions(accounts);
    const calendarNames = calendarNameMap(accounts);
    const blockFromCalendarsNames = blockFromCalendars
        .map(id => calendarNames[id])
        .filter(name => !!name);

    async function doSave(data) {
        const result = await save(data);
        setCalendarIntegrationsQuery(result);
    }

    useEffect(() => {
        setBlockFromCalendars(resource.calendars.map(c => c.id));
        setAddEventToCalendar(resource.settings?.syncToCalendar);
    }, [JSON.stringify(resource.calendars), resource.settings?.syncToCalendar]);

    return (
        <div className="mb-4 panel">
            <div className="px-6 py-4 border-b">{resource.name}</div>
            <div>
                <div
                    className="flex justify-between items-center cursor-pointer ml-6 pr-6 border-b py-4"
                    onClick={() => setIsAddEventsOpen(true)}
                >
                    <div className="flex">
                        <CalendarAddIcon className="text-green-500 w-5 h-5 mr-4 mt-0.5" />
                        <div>
                            <div className="mr-4 min-w-40">Add events to</div>
                            <div className="text-gray-500">
                                {calendarNames[addEventToCalendar] || (
                                    <span className="text-red-600">
                                        Not adding Breely events to a calendar
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button type="button" className="btn-light-pill min-w-16">
                        Edit
                    </button>
                </div>
                <AddEventsToCalendarModal
                    isOpen={isAddEventsOpen}
                    onClose={() => setIsAddEventsOpen(false)}
                    accountOptions={accountOptions}
                    value={addEventToCalendar || ''}
                    setValue={value => {
                        setAddEventToCalendar(value);
                    }}
                    onSave={async () => {
                        await doSave({
                            blockingCalendars: blockFromCalendars,
                            addEventToCalendar: addEventToCalendar,
                        });
                    }}
                />

                <div
                    className="ml-6 pr-6 py-4 cursor-pointer flex justify-between items-center"
                    onClick={() => setIsConflictsOpen(true)}
                >
                    <div className="flex items-start">
                        <CalendarDisableIcon className="text-red-500 w-5 h-5 mr-4 mt-0.5 flex-shrink-0" />
                        <div>
                            Check for conflicts on
                            <div className="text-gray-500">
                                {!blockFromCalendarsNames?.length
                                    ? 'Not blocking off time in Breely'
                                    : ''}
                                {blockFromCalendarsNames.map(name => (
                                    <div>{name}</div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button type="button" className="btn-light-pill min-w-16">
                        Edit
                    </button>
                </div>
                <CheckConflictsModal
                    isOpen={isConflictsOpen}
                    onClose={() => setIsConflictsOpen(false)}
                    accountOptions={accountOptions}
                    value={blockFromCalendars}
                    setValue={value => {
                        setBlockFromCalendars(value);
                    }}
                    onSave={async () => {
                        await doSave({
                            blockingCalendars: blockFromCalendars,
                            addEventToCalendar: addEventToCalendar,
                        });
                    }}
                />
            </div>
        </div>
    );
};

export default BookableResourceSyncing;
